/**
 * One automatic pass over the three stores.
 *
 * Headless twins of the three screens' `sync()`: the same pipeline — read the
 * live window, keep only what the store does not already agree with, write it —
 * without a screen to report to. Each reads a SHORT window (see
 * AUTO_SYNC_WINDOW_DAYS); reaching further back stays a person's job.
 *
 * ── ONE CHANNEL FAILING MUST NOT STOP THE OTHER TWO ──────────────────────
 * Each channel runs in its own try/catch and reports its own result. A
 * scheduler that dies quietly on the first error is the failure mode the
 * outbox services already have (they swallow errors, so exports go out blank
 * and say nothing), and it is not repeated here: every outcome — ok, skipped,
 * failed — is recorded with its reason and shown on the channel's own screen.
 *
 * ── WHAT IT WRITES ───────────────────────────────────────────────────────
 * The same three allow-listed lists the buttons write (TIKTOK_Orders,
 * IGCOM_Orders, LAZADA_Orders) and nothing else, with `syncedBy` marked
 * "(auto)" so the list itself says which rows a person pressed and which a
 * timer did. No contact detail is stored; toStoredOrder is the same single
 * choke point it is for a manual sync.
 */
import { tiktokOrdersService } from "../../lib/sp/TiktokOrdersService";
import { changedOrders as tiktokChanged, toStoredOrder as tiktokStored } from "../../lib/sp/tiktokOrdersStore";
import { fetchTiktokOverview } from "../crm-tiktok/tiktokClient";
import { clearTiktokCache } from "../crm-tiktok/tiktokCache";
import { igcomOrdersService } from "../../lib/sp/IgcomOrdersService";
import { changedOrders as igcomChanged, toStoredOrder as igcomStored } from "../../lib/sp/igcomOrdersStore";
import { fetchIgcomOverview } from "../crm-igcom/igcomClient";
import { clearIgcomCache } from "../crm-igcom/igcomCache";
import { lazadaOrdersService, changedOrders as lazadaChanged } from "../../lib/sp/LazadaOrdersService";
import { fetchLazadaOrders } from "../crm-lazada/lazadaClient";
import { clearLazadaCache } from "../crm-lazada/lazadaCache";
import { ensureLazadaAccessToken, fromApi as lazadaFromApi } from "../crm-lazada/lazadaSyncShared";
import { AUTO_SYNC_WINDOW_DAYS, explainError, igcomBlocked } from "./autoSyncPlan";
import {
  notifyAutoWrote, recordAutoResult, setAutoSyncRunning,
  type AutoChannel, type IAutoResult,
} from "./autoSyncStore";

export type { AutoChannel, IAutoResult } from "./autoSyncStore";

type Outcome = Pick<IAutoResult, "status" | "seen" | "written" | "note">;

const skipped = (note: string): Outcome => ({ status: "skipped", seen: 0, written: 0, note });

const NOT_CREATED = (list: string, screen: string): string =>
  `${list} does not exist yet — open ${screen} and create it there first.`;

async function syncTiktok(by: string, days: number): Promise<Outcome> {
  const live = await fetchTiktokOverview(days);
  const incoming = live.orders.map(tiktokStored);
  const store = await tiktokOrdersService.readStore();
  if (store.missing) return skipped(NOT_CREATED("TIKTOK_Orders", "TikTok Shop"));
  const written = await tiktokOrdersService.writeOrders(tiktokChanged(incoming, store.orders), by);
  if (written > 0) clearTiktokCache();
  return { status: "ok", seen: incoming.length, written, note: "" };
}

async function syncIgcom(by: string, days: number): Promise<Outcome> {
  const live = await fetchIgcomOverview(days);
  // Decided AFTER the read because only the answer knows whether this caller was
  // served real emails or masked ones — see igcomBlocked.
  const blocked = igcomBlocked(live.piiUnmasked);
  if (blocked) return skipped(blocked);
  const incoming = live.orders.map(igcomStored);
  const store = await igcomOrdersService.readStore();
  if (store.missing) return skipped(NOT_CREATED("IGCOM_Orders", "IG COM"));
  const written = await igcomOrdersService.writeOrders(igcomChanged(incoming, store.orders), by);
  if (written > 0) clearIgcomCache();
  return { status: "ok", seen: incoming.length, written, note: "" };
}

async function syncLazada(by: string, days: number): Promise<Outcome> {
  // Renews the token pair if it is nearly out — the same function the screen
  // uses, so there is one ordering for a write that Lazada will not let us redo.
  const token = await ensureLazadaAccessToken(by);
  const answer = await fetchLazadaOrders({ accessToken: token, days });
  const incoming = answer.orders.map(lazadaFromApi);
  const store = await lazadaOrdersService.readStore();
  if (store.missing) return skipped(NOT_CREATED("LAZADA_Orders", "Lazada Shop"));
  const written = await lazadaOrdersService.writeOrders(lazadaChanged(incoming, store.orders), by);
  if (written > 0) clearLazadaCache();
  return { status: "ok", seen: incoming.length, written, note: "" };
}

const CHANNELS: { channel: AutoChannel; run: (by: string, days: number) => Promise<Outcome> }[] = [
  { channel: "tiktok", run: syncTiktok },
  { channel: "igcom", run: syncIgcom },
  { channel: "lazada", run: syncLazada },
];

/**
 * Run all three, one after another. One after another, not together: they
 * share the browser's connections and the SharePoint site's throttle, and the
 * point of a background pass is that nobody is waiting on it.
 *
 * `me` is the signed-in address; the written rows carry "<me> (auto)".
 */
export async function runAutoSync(
  me: string,
  days: number = AUTO_SYNC_WINDOW_DAYS,
): Promise<IAutoResult[]> {
  const by = `${me} (auto)`;
  const results: IAutoResult[] = [];
  setAutoSyncRunning(true);
  try {
    for (const { channel, run } of CHANNELS) {
      let outcome: Outcome;
      try {
        outcome = await run(by, days);
      } catch (e) {
        outcome = { status: "failed", seen: 0, written: 0, note: explainError(e) };
      }
      const result: IAutoResult = { channel, at: new Date().toISOString(), ...outcome };
      results.push(result);
      recordAutoResult(result);
      if (result.status === "ok" && result.written > 0) notifyAutoWrote(channel);
    }
  } finally {
    setAutoSyncRunning(false);
  }
  return results;
}
